import React, { useId, useState } from 'react';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import Section from '@hero/ui-kit/layout/Section';
import useTheme from '@hero/branding/theme';
import MarkModal from '../../../components/MarkModal';
import { MarkButton, MarkLabel } from '../../../components/MarkControls';
import useUnsavedChangesRegistration from '../../../hooks/useUnsavedChangesRegistration';
import type { SegmentAttribute, SegmentFilter, SegmentFilterOperator } from '../../../types';
import AttributeSelect from './AttributeSelect';
import OperatorSelect from './OperatorSelect';
import ValuesInput from './ValuesInput';
import {
    defaultValuesForOperator,
    validateSegmentFilters,
    valuesAreCompatible
} from './validation';
import * as styles from './style.module.css';

type Props = {
    initialFilter?: SegmentFilter;
    attributes: SegmentAttribute[];
    onSave: (filter: SegmentFilter) => void;
    onClose: () => void;
};

const FilterEditorModal: React.FC<Props> = ({ initialFilter, attributes, onSave, onClose }) => {
    const formId = useId();
    const { colors, borders } = useTheme();
    const [initial] = useState<SegmentFilter>(() => ({
        attribute: initialFilter?.attribute ?? '',
        operator: initialFilter?.operator ?? 'in',
        values: [...(initialFilter?.values ?? [])]
    }));
    const [filter, setFilter] = useState(initial);
    const [hasPendingValue, setHasPendingValue] = useState(false);
    const dirty = hasPendingValue || JSON.stringify(filter) !== JSON.stringify(initial);
    const clearUnsaved = useUnsavedChangesRegistration(`segment-filter-${formId}`, dirty);
    const attribute = attributes.find((candidate) => candidate.name === filter.attribute);
    const errors = validateSegmentFilters([filter], attributes);
    const title = initialFilter ? 'Edit filter' : 'Add filter';
    const labelId = `${formId}-value-label`;
    const valueId = `${formId}-value`;

    const changeAttribute = (name: string) => {
        setHasPendingValue(false);
        const selected = attributes.find((candidate) => candidate.name === name);
        const operator = selected?.operators[0] ?? 'in';
        setFilter({ attribute: name, operator, values: defaultValuesForOperator(operator) });
    };
    const changeOperator = (operator: SegmentFilterOperator) => {
        setHasPendingValue(false);
        setFilter((current) => ({
            ...current,
            operator,
            values: valuesAreCompatible(current.operator, operator)
                ? current.values
                : defaultValuesForOperator(operator)
        }));
    };

    return (
        <MarkModal
            aria-label={title}
            onClose={onClose}
            dirty={dirty}
            styles={{ width: 'min(72rem, 90vw)' }}
        >
            <form
                className={styles.filterForm}
                onSubmit={(event) => {
                    event.preventDefault();
                    if (errors.length || hasPendingValue) return;
                    clearUnsaved();
                    onSave(filter);
                }}
            >
                <header>
                    <H role="h4" styles={{ marginBottom: '0.8rem' }}>
                        {title}
                    </H>
                    <P noDefaultMargin styles={{ color: colors.neutrals.textBeta }}>
                        Choose a field and a condition to narrow your audience.
                    </P>
                </header>
                <div className={styles.fieldGrid}>
                    <AttributeSelect
                        value={filter.attribute}
                        attributes={attributes}
                        onChange={changeAttribute}
                        id={`${formId}-attribute`}
                    />
                    <OperatorSelect
                        value={filter.operator}
                        operators={attribute?.operators ?? []}
                        onChange={changeOperator}
                        id={`${formId}-operator`}
                    />
                </div>
                {attribute ? (
                    <div className={styles.field}>
                        <MarkLabel
                            id={labelId}
                            htmlFor={filter.operator === 'between' ? `${valueId}-start` : valueId}
                        >
                            {filter.operator === 'is_null' ? 'Value availability' : 'Values'}
                        </MarkLabel>
                        <ValuesInput
                            key={`${filter.attribute}-${filter.operator}`}
                            operator={filter.operator}
                            attributeType={attribute.type}
                            attributeValues={attribute.values ?? null}
                            values={filter.values}
                            onChange={(values) => setFilter((current) => ({ ...current, values }))}
                            idPrefix={valueId}
                            labelId={labelId}
                            onPendingChange={setHasPendingValue}
                        />
                    </div>
                ) : (
                    <Section padding="small" border styles={{ ...borders.variant.secondary }}>
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{ color: colors.neutrals.textBeta }}
                        >
                            Choose a field to see the available conditions and values.
                        </P>
                    </Section>
                )}
                {filter.attribute && (errors.length > 0 || hasPendingValue) && (
                    <P
                        role="status"
                        noDefaultMargin
                        size="small"
                        styles={{ color: colors.neutrals.textBeta }}
                    >
                        {hasPendingValue
                            ? 'Add the value above before saving this filter.'
                            : errors[0].message}
                    </P>
                )}
                <div className={styles.actions}>
                    <MarkButton variant="secondary" size="regular" onClick={onClose}>
                        Cancel
                    </MarkButton>
                    <MarkButton
                        type="submit"
                        variant="primary"
                        size="regular"
                        disabled={errors.length > 0 || hasPendingValue}
                    >
                        {initialFilter ? 'Save filter' : 'Add filter'}
                    </MarkButton>
                </div>
            </form>
        </MarkModal>
    );
};

export default FilterEditorModal;
